"use client";

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { X } from "lucide-react";

import { regionFromPathname } from "@/lib/region";
import { useRegionSwitchHrefs } from "@/lib/region-paths-context";
import { hasSettledRegionChoice, settleRegionChoice } from "@/lib/region-switch";
import { usePublicPathname } from "@/lib/public-pathname";

const useIsoLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;

export function RegionBanner() {
  const pathname = usePublicPathname();
  const hrefs = useRegionSwitchHrefs();
  const region = regionFromPathname(pathname);
  const [show, setShow] = useState(false);
  const visible = show && region === "ie";
  const bannerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let active = true;

    if (region !== "ie" || hasSettledRegionChoice()) {
      queueMicrotask(() => {
        if (active) setShow(false);
      });
      return () => {
        active = false;
      };
    }

    if (
      process.env.NODE_ENV !== "production" &&
      new URLSearchParams(window.location.search).get("region-test") === "uk"
    ) {
      queueMicrotask(() => {
        if (active) setShow(true);
      });
      return () => {
        active = false;
      };
    }

    fetch("/api/geo/")
      .then((r) => r.json())
      .then((d: { country?: string | null }) => {
        if (active && d.country === "GB") setShow(true);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [region]);

  useIsoLayoutEffect(() => {
    if (!visible) return;
    const el = bannerRef.current;
    if (!el) return;
    const root = document.documentElement;
    const apply = () => root.style.setProperty("--region-banner-h", `${el.offsetHeight}px`);
    apply();
    const observer = new ResizeObserver(apply);
    observer.observe(el);
    return () => {
      observer.disconnect();
      root.style.removeProperty("--region-banner-h");
    };
  }, [visible]);

  if (!visible) return null;

  return (
    <div
      ref={bannerRef}
      role="region"
      aria-label="Region suggestion"
      className="bg-navy-03 relative z-60 text-white"
    >
      <div className="mx-auto flex w-full max-w-7xl flex-col gap-3 px-[clamp(1.25rem,4vw,2.5rem)] py-3 md:flex-row md:items-center md:justify-between md:gap-5">
        <p className="text-[15px] leading-snug md:flex-1">
          It looks like you’re visiting from the United Kingdom. View the UK site for local content
          and pricing.
        </p>
        <div className="flex shrink-0 items-center gap-1 self-end md:gap-2 md:self-auto">
          <a
            href={hrefs.uk}
            onClick={settleRegionChoice}
            className="text-navy-03 hover:bg-teal-03 inline-flex h-10 items-center gap-2 rounded-full bg-white px-5 text-sm font-semibold whitespace-nowrap transition-colors"
          >
            <span
              aria-hidden
              className="inline-block size-4 shrink-0 overflow-hidden rounded-full bg-cover bg-center shadow-[0_0_0_1px_rgba(13,10,44,0.12)]"
              style={{ backgroundImage: "url(/assets/region/uk.svg)" }}
            />
            Visit the UK site
          </a>
          <button
            type="button"
            onClick={() => {
              settleRegionChoice();
              setShow(false);
            }}
            aria-label="Dismiss"
            className="flex size-8 items-center justify-center rounded-full text-white/80 transition-colors hover:bg-white/10 hover:text-white"
          >
            <X className="size-4" strokeWidth={2} />
          </button>
        </div>
      </div>
    </div>
  );
}
